import {FC, useEffect, useMemo, useState} from 'react'
import {
  MailCheck,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  UserCheck,
  UsersRound,
  X,
} from 'lucide-react'
import {Modal} from 'react-bootstrap'

import * as API from '../../api'
import {getUserData, isSuperAdmin} from '../../modules/auth'
import DeleteUserModal from './DeleteUserModal'
import UserModal from './UserModal'
import MessageModal from './../utils/MessageModal'

enum USER_LEVEL {
  SUPERADMIN = 2,
  ADMIN = 1,
  MEMBER = 0,
}

function getUserLevel(user: any): USER_LEVEL {
  if (user.is_superadmin) return USER_LEVEL.SUPERADMIN
  if (user.is_admin) return USER_LEVEL.ADMIN
  return USER_LEVEL.MEMBER
}

function getRole(user: any) {
  if (user.is_superadmin) return {key: 'superadmin', label: 'Super Admin'}
  if (user.is_admin) return {key: 'admin', label: 'Administrador'}
  if (user.is_viewer) return {key: 'viewer', label: 'Visualizador'}
  return {key: 'member', label: 'Membro'}
}

function initials(name = '') {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

const UserList: FC = () => {
  const [selectedUser, setSelectedUser] = useState<any>()
  const [users, setUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [modal, showModal] = useState(false)
  const [messageModal, showMessageModal] = useState(false)
  const [deleteModal, showDeleteModal] = useState(false)
  const [maxUsersReached, setMaxUsersReached] = useState(false)
  const [query, setQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')

  function isUserEditable(user: any) {
    const currentUser = getUserData()
    const currentUserLevel = getUserLevel(currentUser)
    const userLevel = getUserLevel(user)

    if (user.guid === currentUser.guid) return false
    if (currentUserLevel === USER_LEVEL.SUPERADMIN) return true
    return currentUserLevel === USER_LEVEL.ADMIN && userLevel === USER_LEVEL.MEMBER
  }

  function updateData() {
    setLoading(true)
    setLoadError(false)

    API.listUsers()
      .then((response) => {
        const responseUsers = response.data.users || []
        const visibleUsers = isSuperAdmin()
          ? responseUsers
          : responseUsers.filter((user: any) => !user.is_superadmin && !user.is_viewer)

        setUsers(visibleUsers)
        setMaxUsersReached(Boolean(response.data.maxUsersReached))
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    updateData()
  }, [])

  const filteredUsers = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('pt-BR')

    return users.filter((user) => {
      const role = getRole(user).key
      const matchesQuery =
        !normalizedQuery ||
        `${user.name || ''} ${user.email || ''}`
          .toLocaleLowerCase('pt-BR')
          .includes(normalizedQuery)
      const matchesRole = roleFilter === 'all' || role === roleFilter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' ? Boolean(user.is_actived) : !user.is_actived)

      return matchesQuery && matchesRole && matchesStatus
    })
  }, [query, roleFilter, statusFilter, users])

  const stats = useMemo(
    () => [
      {label: 'Usuários cadastrados', value: users.length, icon: UsersRound},
      {
        label: 'Acessos ativos',
        value: users.filter((user) => user.is_actived).length,
        icon: UserCheck,
      },
      {
        label: 'Perfis administrativos',
        value: users.filter((user) => user.is_admin || user.is_superadmin).length,
        icon: ShieldCheck,
      },
      {
        label: 'Recebem mailing',
        value: users.filter((user) => user.is_mailling).length,
        icon: MailCheck,
      },
    ],
    [users]
  )

  const hasFilters = Boolean(query || roleFilter !== 'all' || statusFilter !== 'all')
  const companyName = users.find((user) => user.company_name)?.company_name

  return (
    <section className='ua-section'>
      <Modal
        id='modal_message'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-900px ua-admin-modal-dialog'
        show={messageModal}
        onHide={() => showMessageModal(false)}
      >
        <MessageModal
          title='Limite de usuários atingido'
          description='A empresa já utiliza o número máximo de usuários do plano. Para liberar novos acessos, entre em contato com nossa equipe.'
          onClose={() => showMessageModal(false)}
        />
      </Modal>

      <Modal
        id='kt_modal_create_app'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-900px ua-admin-modal-dialog ua-user-modal-dialog'
        show={modal}
        onHide={() => {
          showModal(false)
          setSelectedUser(null)
        }}
      >
        <UserModal
          userData={selectedUser}
          onUserUpdated={() => {
            updateData()
            showModal(false)
            setSelectedUser(null)
          }}
          onUserCreated={() => {
            updateData()
            showModal(false)
            setSelectedUser(null)
          }}
          onClose={() => {
            showModal(false)
            setSelectedUser(null)
          }}
        />
      </Modal>

      <Modal
        id='kt_modal_delete_app'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-900px ua-admin-modal-dialog'
        show={deleteModal}
        onHide={() => {
          showDeleteModal(false)
          setSelectedUser(null)
        }}
      >
        <DeleteUserModal
          userData={selectedUser}
          onClose={() => {
            showDeleteModal(false)
            setSelectedUser(null)
          }}
          onUserDeleted={() => {
            showDeleteModal(false)
            setSelectedUser(null)
            updateData()
          }}
        />
      </Modal>

      <header className='ua-section__heading'>
        <div className='ua-section__heading-copy'>
          <span className='ua-section__heading-icon'>
            <UsersRound size={20} />
          </span>
          <div>
            <h2>Usuários</h2>
            <p>Consulte cadastros, acompanhe status e gerencie os acessos da equipe.</p>
          </div>
        </div>
        <button
          type='button'
          className='ua-primary-button'
          onClick={() => (maxUsersReached ? showMessageModal(true) : showModal(true))}
        >
          <Plus size={16} />
          Novo usuário
        </button>
      </header>

      <div className='ua-stats'>
        {stats.map((stat) => {
          const Icon = stat.icon
          return (
            <div className='ua-stat' key={stat.label}>
              <span className='ua-stat__icon'>
                <Icon size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{stat.value}</strong>
                <small className='ua-stat__label'>{stat.label}</small>
              </span>
            </div>
          )
        })}
      </div>

      <div className='ua-toolbar'>
        <div className='ua-field ua-field--wide'>
          <label htmlFor='user-search'>Buscar</label>
          <div className='ua-search'>
            <Search size={15} />
            <input
              id='user-search'
              type='search'
              value={query}
              placeholder='Nome ou e-mail do usuário'
              onChange={(event) => setQuery(event.target.value)}
            />
            {query && (
              <button
                type='button'
                className='ua-search__clear'
                aria-label='Limpar busca'
                onClick={() => setQuery('')}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <div className='ua-field'>
          <label htmlFor='user-role-filter'>Perfil</label>
          <div className='ua-select'>
            <select
              id='user-role-filter'
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
            >
              <option value='all'>Todos os perfis</option>
              <option value='admin'>Administradores</option>
              <option value='member'>Membros</option>
              <option value='viewer'>Visualizadores</option>
              <option value='superadmin'>Super Admins</option>
            </select>
          </div>
        </div>
        <div className='ua-field'>
          <label htmlFor='user-status-filter'>Status</label>
          <div className='ua-select'>
            <select
              id='user-status-filter'
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value='all'>Todos os status</option>
              <option value='active'>Ativos</option>
              <option value='inactive'>Desativados</option>
            </select>
          </div>
        </div>
        {hasFilters && (
          <button
            type='button'
            className='ua-secondary-button'
            onClick={() => {
              setQuery('')
              setRoleFilter('all')
              setStatusFilter('all')
            }}
          >
            <X size={14} />
            Limpar
          </button>
        )}
      </div>

      <div className='ua-card'>
        <div className='ua-card__header'>
          <div>
            <h3>{companyName || 'Equipe da empresa'}</h3>
            <p>
              {filteredUsers.length} de {users.length}{' '}
              {users.length === 1 ? 'usuário encontrado' : 'usuários encontrados'}
            </p>
          </div>
          {maxUsersReached && (
            <span className='ua-badge ua-badge--warning'>Limite do plano atingido</span>
          )}
        </div>

        {loading ? (
          <div className='ua-loading'>
            <span className='ua-spinner' /> Carregando usuários…
          </div>
        ) : loadError ? (
          <div className='ua-empty'>
            <div>
              <span className='ua-empty__icon'>
                <UsersRound size={22} />
              </span>
              <h3>Não foi possível carregar os usuários</h3>
              <p>Tente novamente. Se o problema continuar, verifique sua conexão.</p>
              <button type='button' className='ua-secondary-button mt-4' onClick={updateData}>
                Tentar novamente
              </button>
            </div>
          </div>
        ) : filteredUsers.length ? (
          <div className='ua-table-wrap'>
            <table className='ua-table'>
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Perfil</th>
                  <th>Status</th>
                  <th>Mailing</th>
                  <th aria-label='Ações' />
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const role = getRole(user)
                  return (
                    <tr key={user.guid}>
                      <td>
                        <div className='ua-user-cell'>
                          <span className='ua-avatar'>
                            {user.src ? <img src={user.src} alt='' /> : initials(user.name)}
                          </span>
                          <span className='ua-user-cell__copy'>
                            <strong>{user.name}</strong>
                            <small>{user.email}</small>
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`ua-badge ${
                            role.key === 'admin' || role.key === 'superadmin'
                              ? 'ua-badge--warning'
                              : ''
                          }`}
                        >
                          {role.label}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`ua-badge ${
                            user.is_actived ? 'ua-badge--success' : 'ua-badge--danger'
                          }`}
                        >
                          {user.is_actived ? 'Ativo' : 'Desativado'}
                        </span>
                      </td>
                      <td>{user.is_mailling ? 'Recebe' : 'Não recebe'}</td>
                      <td>
                        {isUserEditable(user) && (
                          <div className='ua-actions'>
                            <button
                              type='button'
                              className='ua-icon-button'
                              title='Editar usuário'
                              aria-label={`Editar ${user.name}`}
                              onClick={() => {
                                setSelectedUser(user)
                                showModal(true)
                              }}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type='button'
                              className='ua-icon-button ua-icon-button--danger'
                              title='Remover usuário'
                              aria-label={`Remover ${user.name}`}
                              onClick={() => {
                                setSelectedUser(user)
                                showDeleteModal(true)
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className='ua-empty'>
            <div>
              <span className='ua-empty__icon'>
                <Search size={22} />
              </span>
              <h3>Nenhum usuário encontrado</h3>
              <p>Ajuste os filtros ou limpe a busca para visualizar outros cadastros.</p>
              {hasFilters && (
                <button
                  type='button'
                  className='ua-secondary-button mt-4'
                  onClick={() => {
                    setQuery('')
                    setRoleFilter('all')
                    setStatusFilter('all')
                  }}
                >
                  Limpar filtros
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default UserList
